import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { Footer, SEO } from '@/components';
import { apiJson, fetchApi } from '@/lib/api';
import type { PublicCoachProfileListResponse, UserResponse } from '@/lib/types';
import shipSvg from '@/assets/ship.svg';
import {
  FiArrowLeft,
  FiSearch,
  FiMapPin,
  FiExternalLink,
  FiMail,
  FiFilter,
} from 'react-icons/fi';

const nunitoFont = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

/** Bios longer than this show collapsed with "See more". */
const COACH_BIO_COLLAPSE_CHARS = 280;

type CoachCardData = {
  id: string;
  name: string;
  state: string;
  specialties: string[];
  bio: string;
  website: string;
  headline?: string;
  addedAt: string;
};

function sortCoachesNewestFirst(list: CoachCardData[]): CoachCardData[] {
  return [...list].sort(
    (a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
  );
}

const coaches = [
  {
    id: 12,
    name: 'Cat Climaco',
    state: 'Colorado',
    specialties: ['High-Conflict Divorce', 'Protective Parenting', 'Co-Parent Communication'],
    bio: 'Nationally Certified High-Conflict Divorce & Custody Coach and Colorado Certified Mediator. A domestic abuse survivor who navigated family court successfully, she offers strategy-focused coaching through a trauma-informed lens to help parents protect their children and find peace.',
    website: 'https://www.peacefulpresencedivorcecoaching.com/',
  },
  {
    id: 11,
    name: 'Angela Chu',
    state: 'Washington',
    specialties: ['High-Conflict Dynamics', 'Co-Parenting', 'Cross-Cultural Divorce'],
    bio: 'CDC Certified Divorce Coach® based in the Seattle area, leveraging a background in public health, patient advocacy, and tech. Helps individuals and co-parents navigate conflict with clarity, resilience, and a child-first lens.',
    website: 'https://www.angelachu.coach/',
  },
  {
    id: 1,
    name: 'Tessa Noel',
    state: 'California',
    specialties: ['High-Conflict Co-Parenting', 'Parenting Plans', 'Co-Parent Communication'],
    bio: 'CDC Certified Divorce Coach® and Co-Parenting Specialist who navigated her own high-conflict divorce. Helps mothers move through divorce with clarity, confidence, and peace of mind.',
    website: 'https://www.kindmamadivorcecoaching.com/',
  },
  {
    id: 2,
    name: 'Rosalind Sedacca',
    state: 'Florida',
    specialties: ['Child-Centered Divorce', 'Co-Parenting', 'Divorce Recovery'],
    bio: 'Founder of the Child-Centered Divorce Network with 20+ years of coaching experience. Internationally recognized author and media expert on protecting children during divorce.',
    website: 'https://www.childcentereddivorce.com/',
  },
  {
    id: 3,
    name: 'Meg Gluckman',
    state: 'Washington',
    specialties: ['Co-Parenting', 'Collaborative Divorce', 'Post-Divorce Transition'],
    bio: 'Certified coach and out-of-court divorce specialist based in the Puget Sound area. Offers individual and joint co-parent coaching to help families move forward with calm and clarity.',
    website: 'https://www.meggluckman.com/',
  },
  {
    id: 4,
    name: 'Lyerly Spongberg',
    state: 'Connecticut',
    specialties: ['High-Conflict Divorce', 'Conflict Resolution', 'Co-Parenting Plans'],
    bio: 'DCA Certified ADR Divorce Coach, Certified Co-Parenting Specialist, and CTA Certified Life Coach. Focuses on minimizing trauma and empowering strategic decision-making during divorce.',
    website: 'https://stepupwithlyerly.com/',
  },
  {
    id: 5,
    name: 'Heather Harman',
    state: 'Washington',
    specialties: ['Co-Parenting', 'Stepfamily Coaching', 'Parenting Plans'],
    bio: 'Seattle-based divorce and co-parent coach helping two-home families find harmony. Specializes in collaborative divorce, LGBTQ family support, and parent-teen mediation.',
    website: 'https://www.heatherharmancoaching.com/',
  },
  {
    id: 6,
    name: 'Meghan Remington',
    state: 'Ohio',
    specialties: ['Pre-Divorce Coaching', 'Divorce Navigation', 'Post-Divorce Recovery'],
    bio: 'CDC Certified Divorce Coach® with a Master\'s in Clinical Counseling from Ohio State University. Guides clients through every stage of divorce from contemplation to rebuilding.',
    website: 'https://thrivingahead.com/',
  },
  {
    id: 7,
    name: 'Krista Barber',
    state: 'Florida',
    specialties: ['Child-Focused Coaching', 'High-Conflict Dynamics', 'Emotional Regulation'],
    bio: 'CDC Certified Divorce Coach®, Licensed Clinical Social Worker, and Certified Parent Coach with 12+ years of clinical therapy experience. Provides therapy-informed, child-focused divorce support.',
    website: 'https://livefreecoachingcollective.com/',
  },
  {
    id: 8,
    name: 'Heidi Fredrick',
    state: 'Idaho',
    specialties: ['Women\'s Divorce Support', 'Divorce Process', 'Post-Divorce Transition'],
    bio: 'CDC Certified Divorce Coach® based in Boise, helping women navigate every phase of divorce — from evaluating their options to building a confident new beginning.',
    website: 'https://heidifredrick.com/',
  },
  {
    id: 9,
    name: 'Amy Nielsen',
    state: 'California',
    specialties: ['Divorce Coaching', 'Co-Parenting', 'Custody Mediation'],
    bio: 'Former family law attorney with over a decade of legal experience turned divorce and co-parenting coach. Blends legal knowledge with training in dispute resolution and child development.',
    website: 'https://www.amynielsencoaching.com/',
  },
  {
    id: 10,
    name: 'Dr. Paula Fendley',
    state: 'Texas',
    specialties: ['Divorce Coaching', 'Life Transitions', 'Emotional Recovery'],
    bio: 'Certified divorce coach in Houston with 37+ years of experience working with adults and teens. Helps clients break down the divorce transition into manageable steps with compassion and clarity.',
    website: 'https://www.paulafendley.com/',
  },
];

// Header
function CoachesHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <img src={shipSvg} alt='' className='w-5 h-5 mb-1 mr-2' />
            <span>Harbor</span>
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
            style={{ ...nunitoFont, fontWeight: 400 }}
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

// Coach Card
function CoachCard({ coach }: { coach: CoachCardData }) {
  const [bioExpanded, setBioExpanded] = useState(false);
  const bioText = coach.headline ? `${coach.headline}. ${coach.bio}` : coach.bio;
  const isLongBio = bioText.length > COACH_BIO_COLLAPSE_CHARS;

  return (
    <div className='bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow'>
      <div className='flex items-start justify-between mb-3'>
        <h3
          className='text-xl font-bold text-gray-900'
          style={{ ...nunitoFont, fontWeight: 700 }}
        >
          {coach.name}
        </h3>
        <span className='flex items-center gap-1 text-sm text-gray-500 whitespace-nowrap ml-2'>
          <FiMapPin className='w-3.5 h-3.5' />
          {coach.state}
        </span>
      </div>

      <div className='mb-4'>
        <p
          className={`text-gray-600 text-sm leading-relaxed ${isLongBio && !bioExpanded ? 'line-clamp-5' : ''}`}
          style={{ ...nunitoFont, fontWeight: 400 }}
        >
          {coach.headline ? <span className='font-semibold'>{coach.headline}. </span> : null}
          {coach.bio}
        </p>
        {isLongBio ? (
          <button
            type='button'
            onClick={() => {
              setBioExpanded((open) => {
                const next = !open;
                track('Click Coach Bio See More', {
                  coach: coach.name,
                  state: coach.state,
                  expanded: next,
                });
                return next;
              });
            }}
            className='mt-2 text-primary hover:text-primary-hover font-semibold text-sm transition-colors'
            style={{ ...nunitoFont, fontWeight: 600 }}
          >
            {bioExpanded ? 'See less' : 'See more'}
          </button>
        ) : null}
      </div>

      <div className='flex flex-wrap gap-2 mb-4'>
        {coach.specialties.map((s) => (
          <span
            key={s}
            className='bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full'
            style={{ ...nunitoFont, fontWeight: 600 }}
          >
            {s}
          </span>
        ))}
      </div>

      <a
        href={coach.website}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => track('Click Coach Website', { coach: coach.name, state: coach.state })}
        className='inline-flex items-center gap-1.5 text-primary hover:text-primary-hover font-semibold text-sm transition-colors'
        style={{ ...nunitoFont, fontWeight: 600 }}
      >
        Visit Website <FiExternalLink className='w-3.5 h-3.5' />
      </a>
    </div>
  );
}

// Main Page
function CoachesPage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const publicCoachesQuery = useQuery({
    queryKey: ['public-coaches'],
    queryFn: () => fetchApi<PublicCoachProfileListResponse>('/v1/public/coaches'),
    retry: 1,
  });

  const proUserQuery = useQuery({
    queryKey: ['coaches-cta-professional-user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/professional/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 0,
  });

  useEffect(() => {
    track('Coaches Directory Screen');
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    clearTimeout(searchTimerRef.current);
    if (value.trim()) {
      searchTimerRef.current = setTimeout(() => {
        track('Search Coaches', { query: value.trim() });
      }, 500);
    }
  }, []);

  const handleStateFilter = useCallback((value: string) => {
    setSelectedState(value);
    track('Filter Coaches by State', { state: value || 'All' });
  }, []);

  const handleSpecialtyFilter = useCallback((value: string) => {
    setSelectedSpecialty(value);
    track('Filter Coaches by Specialty', { specialty: value || 'All' });
  }, []);

  const apiCoaches: CoachCardData[] = (publicCoachesQuery.data?.coaches ?? []).map((coach) => ({
    id: `pro-${coach.id}`,
    name: coach.display_name,
    state: coach.state,
    specialties: coach.specialties,
    bio: coach.bio,
    website: coach.website_url,
    headline: coach.headline ?? undefined,
    addedAt: coach.created_at,
  }));
  const staticCoaches: CoachCardData[] = coaches.map((coach, index) => ({
    id: `static-${coach.id}`,
    name: coach.name,
    state: coach.state,
    specialties: coach.specialties,
    bio: coach.bio,
    website: coach.website,
    // Static entries are curated in add order (newest first); approximate timestamps for sorting.
    addedAt: new Date(Date.UTC(2024, coaches.length - 1 - index, 1)).toISOString(),
  }));
  const allCoaches = sortCoachesNewestFirst([...staticCoaches, ...apiCoaches]);
  const allStates = [...new Set(allCoaches.map((c) => c.state))].sort();
  const allSpecialties = [...new Set(allCoaches.flatMap((c) => c.specialties))].sort();

  const filtered = allCoaches.filter((coach) => {
    if (selectedState && coach.state !== selectedState) return false;
    if (selectedSpecialty && !coach.specialties.includes(selectedSpecialty)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        coach.name.toLowerCase().includes(q) ||
        coach.bio.toLowerCase().includes(q) ||
        coach.state.toLowerCase().includes(q) ||
        coach.specialties.some((s) => s.toLowerCase().includes(q))
      );
    }
    return true;
  });
  const isProfessionalSignedIn = proUserQuery.isSuccess;
  const listingCtaHref = isProfessionalSignedIn ? '/pro/directory' : '/auth/pro/sign-up';
  const listingCtaTitle = isProfessionalSignedIn
    ? 'Your profile could be live in the Harbor directory next.'
    : 'Join Harbor and get discovered by families who need support.';
  const listingCtaButtonLabel = isProfessionalSignedIn
    ? 'Complete My Directory Profile'
    : 'Create Free Professional Account';
  const listingCtaSubcopy = isProfessionalSignedIn
    ? 'Add your specialties in under 2 minutes, then submit for review.'
    : 'No co-parent invite required. Sign up and submit your listing for review.';
  // Featured samples should always have content even when API has zero approved listings.
  const featuredCoachSamples = allCoaches.slice(0, 3);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Find a Divorce & Co-Parenting Coach | Harbor'
        description='Connect with certified divorce coaches and co-parenting specialists across the US. Browse coaches by state and specialty to find the right support for your family.'
        path='/coaches'
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Divorce and Co-Parenting Coaches',
          description:
            'Directory of certified divorce coaches and co-parenting specialists',
          itemListElement: allCoaches.map((coach, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Person',
              name: coach.name,
              description: coach.bio,
              url: coach.website,
              jobTitle: 'Divorce Coach',
              address: {
                '@type': 'PostalAddress',
                addressRegion: coach.state,
                addressCountry: 'US',
              },
            },
          })),
        }}
      />
      <CoachesHeader />

      {/* Hero */}
      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='max-w-4xl mx-auto px-6 text-center'>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ ...nunitoFont, fontWeight: 700 }}
          >
            Find a Divorce Coach
          </h1>
          <p
            className='text-xl text-gray-600 max-w-2xl mx-auto'
            style={{ ...nunitoFont, fontWeight: 400 }}
          >
            Connect with certified divorce coaching professionals who can guide
            you through every step of your journey with compassion and expertise.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className='bg-white border-b border-gray-100 py-6'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='flex flex-col md:flex-row gap-4'>
            {/* Search */}
            <div className='relative flex-1'>
              <FiSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
              <input
                type='text'
                placeholder='Search coaches...'
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className='w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
                style={{ ...nunitoFont, fontWeight: 400 }}
              />
            </div>
            {/* State */}
            <div className='relative'>
              <FiMapPin className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
              <select
                value={selectedState}
                onChange={(e) => handleStateFilter(e.target.value)}
                className='pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
                style={{ ...nunitoFont, fontWeight: 400 }}
              >
                <option value=''>All States</option>
                {allStates.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            {/* Specialty */}
            <div className='relative'>
              <FiFilter className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
              <select
                value={selectedSpecialty}
                onChange={(e) => handleSpecialtyFilter(e.target.value)}
                className='pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
                style={{ ...nunitoFont, fontWeight: 400 }}
              >
                <option value=''>All Specialties</option>
                {allSpecialties.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Coach Grid */}
      <section className='py-16 bg-sky-light'>
        <div className='max-w-6xl mx-auto px-6'>
          {filtered.length > 0 ? (
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {filtered.map((coach) => (
                <CoachCard key={coach.id} coach={coach} />
              ))}
            </div>
          ) : (
            <div className='text-center py-16'>
              <p
                className='text-gray-500 text-lg'
                style={{ ...nunitoFont, fontWeight: 400 }}
              >
                No coaches found matching your criteria. Try adjusting your filters.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className='bg-primary py-20'>
        <div className='max-w-4xl mx-auto px-6 text-center'>
          <p
            className='inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/90 mb-4'
            style={{ ...nunitoFont, fontWeight: 700 }}
          >
            Free listing for divorce coaches
          </p>
          <h2
            className='text-3xl md:text-4xl font-bold text-white mb-4'
            style={{ ...nunitoFont, fontWeight: 700 }}
          >
            Grow Your Practice with Harbor
          </h2>
          <p
            className='text-xl text-white/80 mb-8 max-w-2xl mx-auto'
            style={{ ...nunitoFont, fontWeight: 400 }}
          >
            {listingCtaTitle}
          </p>
          <p
            className='text-white/70 text-sm mb-8 max-w-xl mx-auto'
            style={{ ...nunitoFont, fontWeight: 600 }}
          >
            {listingCtaSubcopy}
          </p>
          <div className='grid gap-3 md:grid-cols-3 mb-8 text-left'>
            {featuredCoachSamples.map((coach) => (
              <a
                key={coach.id}
                href={coach.website}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() =>
                  track('Click Featured Coach Sample', {
                    coach: coach.name,
                    state: coach.state,
                  })
                }
                className='group rounded-xl border border-white/30 bg-white/10 p-4 hover:bg-white/20 transition-colors'
              >
                <p
                  className='text-white text-sm font-bold mb-1 group-hover:underline'
                  style={{ ...nunitoFont, fontWeight: 700 }}
                >
                  {coach.name}
                </p>
                <p
                  className='text-white/80 text-xs mb-2'
                  style={{ ...nunitoFont, fontWeight: 600 }}
                >
                  {coach.state}
                </p>
                <p
                  className='text-white/70 text-xs line-clamp-2'
                  style={{ ...nunitoFont, fontWeight: 500 }}
                >
                  {coach.specialties.slice(0, 2).join(' • ')}
                </p>
              </a>
            ))}
          </div>
          <a
            href={listingCtaHref}
            onClick={() =>
              track('Click Coach Listing CTA', {
                destination: isProfessionalSignedIn ? 'pro-directory' : 'pro-sign-up',
              })
            }
            className='inline-flex items-center gap-2 bg-white text-primary hover:bg-sky-50 font-semibold py-4 px-8 rounded-xl transition-all duration-150 shadow-lg shadow-primary/30 hover:-translate-y-0.5'
            style={{ ...nunitoFont, fontWeight: 600 }}
          >
            <FiMail className='w-5 h-5' />
            {listingCtaButtonLabel}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/coaches')({
  component: CoachesPage,
});
